import React from 'react';
import { motion } from 'framer-motion';
import { FaTrophy, FaUsers, FaDumbbell, FaFire } from 'react-icons/fa';
import NumberCounter from 'number-counter';
import image1 from '../../assets/karthikeyan1.jpeg';
import image2 from '../../assets/karthikeyan.png';
import image3 from '../../assets/image3.png';

const WhoWeAre = () => {
  const transition = { type: 'spring', duration: 2.5 };

  const fadeUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition },
  };
  const fadeLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0, transition },
  };
  const fadeRight = {
    hidden: { opacity: 0, x: 60 },
    visible: { opacity: 1, x: 0, transition },
  };

  const stats = [
    { end: 2, start: 1, label: 'Expert Coaches', icon: <FaTrophy /> },
    { end: 978, start: 800, label: 'Happy Members', icon: <FaUsers /> },
    { end: 9, start: 3, label: 'Workout Blueprints', icon: <FaDumbbell /> },
    { end: 8, start: 1, label: 'Years of Excellence', icon: <FaFire /> },
  ];

  return (
    <section className="relative px-4 md:px-12 py-16 text-white overflow-hidden max-w-7xl mx-auto flex flex-col gap-14" id="who-we-are">
      {/* Blur background */}
      <div className="absolute w-[20rem] h-[20rem] bg-red-600/20 blur-[160px] rounded-full top-10 left-0 -z-10" />

      {/* Section Header */}
      <motion.div
        className="text-center max-w-3xl mx-auto"
        initial="hidden"
        whileInView="visible"
        variants={fadeUp}
        viewport={{ once: true }}
      >
        <span className="inline-block bg-red-600/15 border border-red-500/30 text-red-500 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-3 shadow-lg">
          Who We Are
        </span>
        <div className="text-3xl sm:text-5xl font-black uppercase tracking-wide mb-4">
          <span className="text-transparent [-webkit-text-stroke:_1.5px_white]">About </span>
          <span className="text-red-500">IDOL FITNESS ZONE</span>
        </div>
        <p className="text-gray-300 text-sm md:text-base leading-relaxed">
          Built around structured training, professional coaching and long-term progress — we don't just make people exercise, we teach them how to train effectively.
        </p>
      </motion.div>

      {/* Content: Images + Story */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
        {/* Image Grid */}
        <motion.div
          className="grid grid-cols-2 gap-4"
          initial="hidden"
          whileInView="visible"
          variants={fadeLeft}
          viewport={{ once: true }}
        >
          <img src={image1} alt="Gym interior" className="w-full h-full min-h-[260px] object-cover rounded-3xl shadow-xl row-span-2 hover:scale-[1.02] transition-transform duration-300 border border-white/10" />
          <img src={image2} alt="Training session" className="w-full h-44 sm:h-56 object-cover rounded-3xl shadow-xl hover:scale-[1.02] transition-transform duration-300 border border-white/10" />
          <img src={image3} alt="Fitness class" className="w-full h-44 sm:h-56 object-cover rounded-3xl shadow-xl hover:scale-[1.02] transition-transform duration-300 border border-white/10" />
        </motion.div>

        {/* Story Text */}
        <motion.div
          className="flex flex-col gap-6"
          initial="hidden"
          whileInView="visible"
          variants={fadeRight}
          viewport={{ once: true }}
        >
          <span className="text-red-500 font-bold text-xs uppercase tracking-widest">Our Purpose</span>
          <h2 className="text-2xl sm:text-4xl font-black uppercase leading-tight">
            Training With <span className="text-transparent [-webkit-text-stroke:_1px_white]">Purpose & Science</span>
          </h2>
          <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
            IDOL Fitness Zone is built around structured training, professional coaching and long-term progress.
            The goal is not simply to make people exercise — it is to teach them how to train effectively.
            Every member follows a personalised path grounded in science-backed methodology.
          </p>

          {/* Coaches */}
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-4 bg-white/5 p-4 rounded-2xl border border-white/10 backdrop-blur-md hover:border-red-500/40 transition-colors">
              <FaTrophy className="text-red-500 text-2xl flex-shrink-0 mt-1" />
              <div>
                <p className="text-white font-bold text-sm">Karthikeyan — Owner & Head Coach</p>
                <p className="text-gray-400 text-xs leading-relaxed mt-0.5">Professional fitness coach focused on structured, science-backed training and long-term client progress.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white/5 p-4 rounded-2xl border border-white/10 backdrop-blur-md hover:border-red-500/40 transition-colors">
              <FaTrophy className="text-red-500 text-2xl flex-shrink-0 mt-1" />
              <div>
                <p className="text-white font-bold text-sm">Sanjay — Owner & Head Coach</p>
                <p className="text-gray-400 text-xs leading-relaxed mt-0.5">Professional fitness coach focused on structured, science-backed training and long-term client progress.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
        {stats.map((stat, i) => (
          <motion.div
            key={i}
            className="flex flex-col items-center justify-center p-5 sm:p-6 bg-gradient-to-b from-[#181a24]/90 to-[#12141c]/90 backdrop-blur-lg rounded-3xl border border-white/10 text-center hover:border-red-500/50 transition-all duration-300 shadow-xl"
            initial="hidden"
            whileInView="visible"
            variants={fadeUp}
            viewport={{ once: true, margin: '-50px' }}
            whileHover={{ y: -6 }}
          >
            <span className="text-red-500 text-2xl sm:text-3xl mb-2">{stat.icon}</span>
            <span className="text-2xl sm:text-4xl font-extrabold text-white">
              <NumberCounter end={stat.end} start={stat.start} delay="3" preFix="+" />
            </span>
            <span className="text-gray-400 text-[10px] sm:text-xs font-bold uppercase tracking-wider mt-1">{stat.label}</span>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default WhoWeAre;
